import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Photo, PhotoUploadLimit, CreatePhotoRequest, UpdatePhotoRequest } from '@/shared/api'
import { PhotoService } from '@/modules/photos'
import { useAuth } from '@/modules/auth'
import { supabase } from '@/shared/utils/supabase'

interface UsePhotosReturn {
  photos: Photo[]
  uploadLimit: PhotoUploadLimit | null
  isLoading: boolean
  isUploading: boolean
  isUpdating: boolean
  isDeleting: boolean
  error: string | null
  createPhoto: (photoData: CreatePhotoRequest) => Promise<void>
  updatePhoto: (photoId: string, updateData: UpdatePhotoRequest) => Promise<void>
  deletePhoto: (photoId: string) => Promise<void>
  refetchPhotos: () => Promise<void>
  refetchUploadLimit: () => Promise<void>
}

export function usePhotos(tripId: string): UsePhotosReturn {
  const [photos, setPhotos] = useState<Photo[]>([])
  const [uploadLimit, setUploadLimit] = useState<PhotoUploadLimit | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const subscriptionRef = useRef<any>(null)
  // Track recently uploaded photo IDs to prevent real-time subscription from interfering
  const recentlyUploadedRef = useRef<Set<string>>(new Set())

  // Create photo service instance
  const photoService = useMemo(() => {
    return new PhotoService(getAccessToken)
  }, [getAccessToken])

  const fetchPhotos = useCallback(async () => {
    if (!tripId) return
    
    try {
      setError(null)
      const photos = await photoService.getPhotosByTrip(tripId)
      setPhotos(photos)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch photos'
      setError(errorMessage)
    }
  }, [tripId, photoService])

  const fetchUploadLimit = useCallback(async () => {
    if (!tripId) return
    
    try {
      const limit = await photoService.getPhotoUploadLimit(tripId)
      setUploadLimit(limit)
    } catch (err) {
      // Silently fail for upload limits - not critical for functionality
    }
  }, [tripId, photoService])

  const createPhoto = useCallback(async (photoData: CreatePhotoRequest) => {
    if (!tripId) return
    
    try {
      setIsUploading(true)
      setError(null)
      
      const photo = await photoService.createPhoto(tripId, photoData)
      
      // Validate photo has required fields before adding to state
      if (!photo || !photo.id || !photo.url) {
        throw new Error('Invalid photo data received from server')
      }
      
      // Ensure tripId and uploadedBy are set (should be included in backend response)
      const photoWithRequiredFields: Photo = {
        ...photo,
        tripId: photo.tripId || tripId,
        uploadedBy: photo.uploadedBy || 'unknown',
        uploadedAt: photo.uploadedAt || new Date().toISOString()
      }
      
      // Mark this photo as recently uploaded to prevent real-time subscription from interfering
      recentlyUploadedRef.current.add(photoWithRequiredFields.id)
      // Clear the flag after 5 seconds (enough time for real-time event to arrive and be ignored)
      setTimeout(() => {
        recentlyUploadedRef.current.delete(photoWithRequiredFields.id)
      }, 5000)
      
      // Add the new photo to the beginning of the list
      setPhotos(prev => {
        // Check if photo already exists (avoid duplicates)
        const exists = prev.some(p => p.id === photoWithRequiredFields.id)
        if (exists) {
          // Update existing photo instead of adding duplicate
          return prev.map(p => p.id === photoWithRequiredFields.id ? photoWithRequiredFields : p)
        }
        return [photoWithRequiredFields, ...prev]
      })
      
      // Update upload limit and refresh storage quota
      await fetchUploadLimit()
      queryClient.invalidateQueries({ queryKey: ['subscription', 'storage'] })

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload photo'
      setError(errorMessage)
      throw err
    } finally {
      setIsUploading(false)
    }
  }, [tripId, photoService, fetchUploadLimit, queryClient])

  const updatePhoto = useCallback(async (photoId: string, updateData: UpdatePhotoRequest) => {
    try {
      setIsUpdating(true)
      setError(null)
      
      const photo = await photoService.updatePhoto(photoId, updateData)
      
      // Update the photo in the list
      setPhotos(prev => prev.map(p => 
        p.id === photoId ? photo : p
      ))
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update photo'
      setError(errorMessage)
      
      
      throw err
    } finally {
      setIsUpdating(false)
    }
  }, [photoService])

  const deletePhoto = useCallback(async (photoId: string) => {
    try {
      setIsDeleting(true)
      setError(null)
      
      await photoService.deletePhoto(photoId)
      
      // Remove the photo from the list
      setPhotos(prev => prev.filter(photo => photo.id !== photoId))
      
      // Update upload limit and refresh storage quota
      await fetchUploadLimit()
      queryClient.invalidateQueries({ queryKey: ['subscription', 'storage'] })

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete photo'
      setError(errorMessage)
      throw err
    } finally {
      setIsDeleting(false)
    }
  }, [photoService, fetchUploadLimit, queryClient])

  const refetchPhotos = useCallback(async () => {
    await fetchPhotos()
  }, [fetchPhotos])

  const refetchUploadLimit = useCallback(async () => {
    await fetchUploadLimit()
  }, [fetchUploadLimit])

  // Real-time subscription for photo updates (optimization status changes and new uploads)
  useEffect(() => {
    if (!tripId) return

    // Subscribe to changes in trip_photos table for this specific trip
    const subscription = supabase
      .channel(`trip_photos_${tripId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'trip_photos',
          filter: `trip_id=eq.${tripId}`
        },
        async (payload) => {
          // When a new photo is inserted (e.g., from another session), refetch to get full photo data
          const newPhoto = payload.new as any
          const photoId = newPhoto.id
          
          // Skip if this is a photo we just uploaded ourselves (prevent interference)
          if (recentlyUploadedRef.current.has(photoId)) {
            return
          }
          
          // Only refetch if this photo isn't already in our state (avoid duplicate refetches)
          setPhotos(prev => {
            const exists = prev.some(p => p.id === photoId)
            if (!exists) {
              // Refetch to get the complete photo object with proper URL
              fetchPhotos()
            }
            return prev
          })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'trip_photos',
          filter: `trip_id=eq.${tripId}`
        },
        async (payload) => {
          const updatedPhoto = payload.new as any
          const oldPhoto = payload.old as any
          
          // If optimization status changed to 'completed', refetch photos to get updated URL
          if (
            updatedPhoto.optimization_status === 'completed' &&
            oldPhoto?.optimization_status !== 'completed'
          ) {
            // Refetch photos to get the updated URL with optimized path
            // This ensures the frontend displays the .webp version automatically
            fetchPhotos()
          } else {
            // For other updates, just update the photo in state
            setPhotos(prev => prev.map(p => {
              if (p.id === updatedPhoto.id) {
                return {
                  ...p,
                  optimizationStatus: updatedPhoto.optimization_status || p.optimizationStatus,
                  url: updatedPhoto.file_path ? p.url : p.url // URL will be updated on next refetch
                }
              }
              return p
            }))
          }
        }
      )
      .subscribe()

    subscriptionRef.current = subscription

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current)
        subscriptionRef.current = null
      }
    }
  }, [tripId, fetchPhotos])

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        await Promise.all([fetchPhotos(), fetchUploadLimit()])
      } finally {
        setIsLoading(false)
      }
    }
    
    loadData()
  }, [fetchPhotos, fetchUploadLimit])

  return {
    photos,
    uploadLimit,
    isLoading,
    isUploading,
    isUpdating,
    isDeleting,
    error,
    createPhoto,
    updatePhoto,
    deletePhoto,
    refetchPhotos,
    refetchUploadLimit
  }
}
